import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useSpecialism } from "../../contexts/SpecialismContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../../firebase/config";

interface Card {
  _docId: string;
  frontText: string;
  backText: string;
  frontImageURL: string[];
  backImageURL: string[];
  deck: string;
}

const SWIPE_ANIMATION_DURATION = 400; // ms

const ReadCardPage: React.FC = () => {
  const { deck } = useParams<{ deck: string }>();
  const { specialismCode } = useSpecialism();
  const [cards, setCards] = useState<Card[]>([]);
  const [currentIdx, setCurrentIdx] = useState(0);
  const [isFlipped, setIsFlipped] = useState(false);
  const [swipeDir, setSwipeDir] = useState<"left" | "right" | null>(null);
  const [swipeCards, setSwipeCards] = useState<{ left: Card[]; right: Card[] }>({
    left: [],
    right: [],
  });
  const [fullscreenImageUrl, setFullscreenImageUrl] = useState<string | null>(null);
  
  const cardRef = useRef<HTMLDivElement>(null);
  
  // Add touch gesture states
  const [touchStart, setTouchStart] = useState<{ x: number; y: number } | null>(null);
  const [touchEnd, setTouchEnd] = useState<{ x: number; y: number } | null>(null);

  // Responsive breakpoints
  const [isMobile, setIsMobile] = useState(window.innerWidth < 850);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth < 400);

  // Update responsive state on resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 850);
      setIsSmallMobile(window.innerWidth < 400);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Prevent body scroll when component mounts
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
    
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    if (!specialismCode || !deck) return;
    const fetchCards = async () => {
      const colName = `flashcards_${specialismCode.toLowerCase()}`;
      const snapshot = await getDocs(collection(db, colName));
      const deckCards: Card[] = [];
      snapshot.forEach((docSnap) => {
        const data = docSnap.data();
        if (data.deck === decodeURIComponent(deck) && !data.isPlaceholder) {
          deckCards.push({ ...data, _docId: docSnap.id } as Card);
        }
      });
      setCards(deckCards);
    };
    fetchCards();
  }, [specialismCode, deck]);

  const shuffleArray = (array: Card[]) => {
    for (let i = array.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [array[i], array[j]] = [array[j], array[i]];
    }
    return array;
  };

  const handleGoOverLeftCards = () => {
    const shuffledCards = shuffleArray([...swipeCards.left]);
    setCards((prevCards) => [...shuffledCards, ...prevCards]);
    setSwipeCards((prevSwipeCards) => ({
      left: [],
      right: prevSwipeCards.right,
    }));
    setCurrentIdx(0);
  };

  const handleSwipe = (direction: "left" | "right") => {
    if (swipeDir !== null || currentIdx >= cards.length) return;

    setSwipeDir(direction);
    setTimeout(() => {
      setSwipeCards((sc) => ({
        ...sc,
        [direction]: [...sc[direction], cards[currentIdx]],
      }));
      setIsFlipped(false);
      setSwipeDir(null);
      setCards((prevCards) => prevCards.filter((_, idx) => idx !== currentIdx));
      setCurrentIdx((idx) => Math.min(idx, cards.length - 1));
    }, SWIPE_ANIMATION_DURATION);
  };

  const handleFlip = () => {
    setIsFlipped((prev) => !prev);
  };

  // Touch gesture handlers
  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd({
      x: e.targetTouches[0].clientX,
      y: e.targetTouches[0].clientY,
    });
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const swipeThreshold = 50;
    const horizontalDistance = touchStart.x - touchEnd.x;
    const verticalDistance = touchStart.y - touchEnd.y;
    
    const isHorizontalSwipe = Math.abs(horizontalDistance) > Math.abs(verticalDistance);
    
    if (isHorizontalSwipe && Math.abs(horizontalDistance) > swipeThreshold) {
      if (horizontalDistance > 0) {
        // Swiped left
        handleSwipe("left");
      } else {
        // Swiped right
        handleSwipe("right");
      }
    }
    
    setTouchStart(null);
    setTouchEnd(null);
  };

  // Keyboard event listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        return;
      }

      if (currentIdx >= cards.length) return;
      
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        handleSwipe("left");
      } else if (e.key === "ArrowRight") {
        e.preventDefault();
        handleSwipe("right");
      } else if (e.key === " " || e.key === "Enter" || e.key === "ArrowUp") {
        e.preventDefault();
        handleFlip();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [currentIdx, cards.length, swipeDir, isFlipped]);

  // Responsive card dimensions
  const getCardDimensions = () => {
    if (isSmallMobile) {
      return { width: "280px", height: "380px" };
    } else if (isMobile) {
      return { width: "320px", height: "420px" };
    } else {
      return { width: "340px", height: "440px" };
    }
  };

  const cardDimensions = getCardDimensions();

  // Card stack rendering
  const renderStack = () => {
    return cards.map((card, idx) => {
      if (idx < currentIdx) return null;
      const isTop = idx === currentIdx;
      const isTopFlipped = isTop ? isFlipped : false;

      return (
        <div
          key={card._docId}
          ref={isTop ? cardRef : undefined}
          onTouchStart={isTop ? handleTouchStart : undefined}
          onTouchMove={isTop ? handleTouchMove : undefined}
          onTouchEnd={isTop ? handleTouchEnd : undefined}
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            width: cardDimensions.width,
            height: cardDimensions.height,
            transform: isTop
              ? swipeDir === "left"
                ? `translate(-50%, -50%) translateX(-40vw) translateY(240px) scale(0.3)`
                : swipeDir === "right"
                ? `translate(-50%, -50%) translateX(40vw) translateY(240px) scale(0.3)`
                : `translate(-50%, -50%) scale(1)`
              : `translate(-50%, -50%) scale(${1 - (idx - currentIdx) * 0.04}) translateY(${(idx - currentIdx) * 8}px)`,
            zIndex: 1000 - idx,
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            background: "#fff",
            borderRadius: "18px",
            border: "2px solid #eee",
            cursor: isTop ? "pointer" : "default",
            transition: swipeDir
              ? `transform ${SWIPE_ANIMATION_DURATION}ms cubic-bezier(.55,-0.01,.33,1.01), opacity ${SWIPE_ANIMATION_DURATION}ms`
              : "transform 0.2s, opacity 0.2s",
            opacity: isTop && swipeDir ? 0 : 1,
            pointerEvents: isTop ? "auto" : "none",
            userSelect: "none",
            touchAction: "none",
          }}
          onClick={isTop ? (e) => {
            if ((e.target as HTMLElement).tagName !== 'IMG') {
              handleFlip();
            }
          } : undefined}
        >
          {/* Flip indicator - smaller on mobile */}
          {isTop && (
            <div
              style={{
                position: "absolute",
                top: isSmallMobile ? "8px" : "12px",
                right: isSmallMobile ? "8px" : "12px",
                width: isSmallMobile ? "28px" : "32px",
                height: isSmallMobile ? "28px" : "32px",
                borderRadius: "50%",
                background: "rgba(25, 118, 210, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: isSmallMobile ? "16px" : "18px",
                color: "#1976d2",
                zIndex: 1001,
                pointerEvents: "none",
              }}
            >
              🔄
            </div>
          )}
          <div
            style={{
              width: "100%",
              height: "100%",
              position: "relative",
              perspective: "1200px",
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                borderRadius: "18px",
                background: "#fff",
                boxSizing: "border-box",
                backfaceVisibility: "hidden",
                transition: `transform 0.6s, opacity ${SWIPE_ANIMATION_DURATION}ms`,
                transformStyle: "preserve-3d",
                transform: isTopFlipped ? "rotateY(180deg)" : "rotateY(0deg)",
                opacity: isTop && swipeDir ? 0 : 1,
              }}
            >
              {/* Front */}
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  backfaceVisibility: "hidden",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isMobile ? "24px 16px" : "32px 24px",
                  borderRadius: "18px",
                  background: "#fff",
                  boxSizing: "border-box",
                  opacity: isTop && swipeDir ? 0 : 1,
                  transition: `opacity ${SWIPE_ANIMATION_DURATION}ms`,
                }}
              >
                <div
                  style={{
                    fontSize: isSmallMobile ? "18px" : isMobile ? "20px" : "22px",
                    textAlign: "center",
                    whiteSpace: "pre-line",
                    color: "#222",
                    opacity: isTop && swipeDir ? 0 : 1,
                    transition: `opacity ${SWIPE_ANIMATION_DURATION}ms`,
                  }}
                >
                  {card.frontText}
                </div>
                {card.frontImageURL && card.frontImageURL.length > 0 && (
                  <img
                    src={card.frontImageURL[0]}
                    alt="Front"
                    onClick={() => setFullscreenImageUrl(card.frontImageURL[0])}
                    style={{
                      maxWidth: "90%",
                      maxHeight: "40%",
                      marginTop: "12px",
                      borderRadius: "8px",
                      objectFit: "contain",
                      cursor: isTop ? "pointer" : "default",
                    }}
                  />
                )}
              </div>
              {/* Back */}
              <div
                style={{
                  position: "absolute",
                  width: "100%",
                  height: "100%",
                  backfaceVisibility: "hidden",
                  transform: "rotateY(180deg)",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  padding: isMobile ? "24px 16px" : "32px 24px",
                  borderRadius: "18px",
                  background: "#f7f7f7",
                  boxSizing: "border-box",
                  opacity: isTop && swipeDir ? 0 : 1,
                  transition: `opacity ${SWIPE_ANIMATION_DURATION}ms`,
                }}
              >
                <div
                  style={{
                    fontSize: isSmallMobile ? "18px" : isMobile ? "20px" : "22px",
                    textAlign: "center",
                    whiteSpace: "pre-line",
                    color: "#222",
                    opacity: isTop && swipeDir ? 0 : 1,
                    transition: `opacity ${SWIPE_ANIMATION_DURATION}ms`,
                  }}
                >
                  {card.backText}
                </div>
                {card.backImageURL && card.backImageURL.length > 0 && (
                  <img
                    src={card.backImageURL[0]}
                    alt="Back"
                    onClick={() => setFullscreenImageUrl(card.backImageURL[0])}
                    style={{
                      maxWidth: "90%",
                      maxHeight: "40%",
                      marginTop: "12px",
                      borderRadius: "8px",
                      objectFit: "contain",
                      cursor: isTop ? "pointer" : "default",
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      );
    });
  };

  // Responsive boxes for swiped cards
  const renderSwipedBoxes = () => {
    if (isMobile) {
      return (
        <div
          style={{
            position: "fixed",
            bottom: "15px",
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            flexDirection: "row",
            gap: "8px",
            zIndex: 10,
            maxHeight: isSmallMobile ? "100px" : "120px",
            width: "calc(100% - 20px)",
            maxWidth: "500px",
          }}
        >
          {/* Left Box */}
          <div
            style={{
              flex: 1,
              minWidth: "0",
              maxHeight: isSmallMobile ? "100px" : "120px",
              background: "#f5f5f5",
              borderRadius: "12px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              padding: isSmallMobile ? "8px 4px" : "10px 6px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: isSmallMobile ? "11px" : "13px",
                marginBottom: "4px",
                color: "#1976d2",
                position: "sticky",
                top: isSmallMobile ? "-8px" : "-10px",
                background: "#f5f5f5",
                width: "100%",
                textAlign: "center",
                paddingTop: isSmallMobile ? "8px" : "10px",
                paddingBottom: "6px",
                zIndex: 1,
              }}
            >
              Review ({swipeCards.left.length})
            </div>
            {swipeCards.left.map((card) => (
              <div
                key={card._docId}
                style={{
                  width: "95%",
                  minHeight: isSmallMobile ? "24px" : "28px",
                  margin: "3px 0",
                  padding: "4px",
                  background: "#fff",
                  borderRadius: "6px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  fontSize: isSmallMobile ? "10px" : "11px",
                  color: "#444",
                  textAlign: "center",
                  wordWrap: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {card.frontText}
              </div>
            ))}
          </div>
          {/* Right Box */}
          <div
            style={{
              flex: 1,
              minWidth: "0",
              maxHeight: isSmallMobile ? "100px" : "120px",
              background: "#f5f5f5",
              borderRadius: "12px",
              boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
              padding: isSmallMobile ? "8px 4px" : "10px 6px",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              overflowY: "auto",
              overflowX: "hidden",
            }}
          >
            <div
              style={{
                fontWeight: 600,
                fontSize: isSmallMobile ? "11px" : "13px",
                marginBottom: "4px",
                color: "#4caf50",
                position: "sticky",
                top: isSmallMobile ? "-8px" : "-10px",
                background: "#f5f5f5",
                width: "100%",
                textAlign: "center",
                paddingTop: isSmallMobile ? "8px" : "10px",
                paddingBottom: "6px",
                zIndex: 1,
              }}
            >
              Mastered ({swipeCards.right.length})
            </div>
            {swipeCards.right.map((card) => (
              <div
                key={card._docId}
                style={{
                  width: "95%",
                  minHeight: isSmallMobile ? "24px" : "28px",
                  margin: "3px 0",
                  padding: "4px",
                  background: "#fff",
                  borderRadius: "6px",
                  boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                  fontSize: isSmallMobile ? "10px" : "11px",
                  color: "#444",
                  textAlign: "center",
                  wordWrap: "break-word",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {card.frontText}
              </div>
            ))}
          </div>
        </div>
      );
    }

    return (
      <>
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            left: "8vw",
            width: "320px",
            maxHeight: "200px",
            background: "#f5f5f5",
            borderRadius: "16px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            padding: "18px 12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 10,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "18px",
              marginBottom: "8px",
              color: "#1976d2",
              position: "sticky",
              top: "-18px",
              background: "#f5f5f5",
              width: "100%",
              textAlign: "center",
              paddingTop: "18px",
              paddingBottom: "12px",
              zIndex: 1,
            }}
          >
            Need Review ({swipeCards.left.length})
          </div>
          {swipeCards.left.map((card) => (
            <div
              key={card._docId}
              style={{
                width: "95%",
                minHeight: "36px",
                margin: "6px 0",
                padding: "8px",
                background: "#fff",
                borderRadius: "10px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                fontSize: "16px",
                color: "#444",
                textAlign: "center",
                wordWrap: "break-word",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {card.frontText}
            </div>
          ))}
        </div>
        <div
          style={{
            position: "fixed",
            bottom: "20px",
            right: "8vw",
            width: "320px",
            maxHeight: "200px",
            background: "#f5f5f5",
            borderRadius: "16px",
            boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
            padding: "18px 12px",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            zIndex: 10,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <div
            style={{
              fontWeight: 600,
              fontSize: "18px",
              marginBottom: "8px",
              color: "#4caf50",
              position: "sticky",
              top: "-18px",
              background: "#f5f5f5",
              width: "100%",
              textAlign: "center",
              paddingTop: "18px",
              paddingBottom: "12px",
              zIndex: 1,
            }}
          >
            Mastered ({swipeCards.right.length})
          </div>
          {swipeCards.right.map((card) => (
            <div
              key={card._docId}
              style={{
                width: "95%",
                minHeight: "36px",
                margin: "6px 0",
                padding: "8px",
                background: "#fff",
                borderRadius: "10px",
                boxShadow: "0 1px 4px rgba(0,0,0,0.04)",
                fontSize: "16px",
                color: "#444",
                textAlign: "center",
                wordWrap: "break-word",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {card.frontText}
            </div>
          ))}
        </div>
      </>
    );
  };

  // Main render
  return (
    <div
      style={{
        width: "100vw",
        height: "100vh",
        backgroundColor: "#fff",
        margin: 0,
        padding: 0,
        position: "fixed",
        top: 0,
        left: 0,
        overflow: "hidden",
      }}
    >
      {/* Stack of cards */}
      <div
        style={{
          position: "absolute",
          left: "50%",
          top: isMobile ? "45%" : "50%",
          transform: "translate(-50%, -50%)",
          width: cardDimensions.width,
          height: cardDimensions.height,
          zIndex: 100,
        }}
      >
        {renderStack()}
        {/* Card count indicator */}
        <div
          style={{
            position: "absolute",
            top: isSmallMobile ? "-32px" : "-38px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: isSmallMobile ? "16px" : isMobile ? "18px" : "20px",
            fontWeight: 700,
            color: "#1976d2",
            background: "#fff",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
            padding: isSmallMobile ? "4px 12px" : "6px 18px",
          }}
        >
          {cards.length - currentIdx} / {cards.length}
        </div>
      </div>

      {/* Arrow buttons - only show on desktop */}
      {!isMobile && cards.length > 0 && currentIdx < cards.length && (
        <div
          style={{
            position: "fixed",
            left: "50%",
            bottom: "250px",
            transform: "translateX(-50%)",
            display: "flex",
            gap: "40px",
            zIndex: 150,
          }}
        >
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => handleSwipe("left")}
              disabled={swipeDir !== null}
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: swipeDir !== null ? "#ccc" : "#1976d2",
                color: "#fff",
                fontSize: "24px",
                cursor: swipeDir !== null ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.2s, transform 0.1s",
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.95)"}
              onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
            >
              ←
            </button>
            <div style={{ fontSize: "11px", color: "#666", marginTop: "6px" }}>
              Need Review
            </div>
          </div>
          <div style={{ textAlign: "center" }}>
            <button
              onClick={() => handleSwipe("right")}
              disabled={swipeDir !== null}
              style={{
                width: "60px",
                height: "60px",
                borderRadius: "50%",
                border: "none",
                backgroundColor: swipeDir !== null ? "#ccc" : "#4caf50",
                color: "#fff",
                fontSize: "24px",
                cursor: swipeDir !== null ? "not-allowed" : "pointer",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                transition: "background-color 0.2s, transform 0.1s",
              }}
              onMouseDown={(e) => e.currentTarget.style.transform = "scale(0.95)"}
              onMouseUp={(e) => e.currentTarget.style.transform = "scale(1)"}
              onMouseLeave={(e) => e.currentTarget.style.transform = "scale(1)"}
            >
              →
            </button>
            <div style={{ fontSize: "11px", color: "#666", marginTop: "6px" }}>
              Mastered
            </div>
          </div>
        </div>
      )}

      {/* Mobile swipe indicator */}
      {isMobile && cards.length > 0 && currentIdx < cards.length && (
        <div
          style={{
            position: "fixed",
            bottom: isSmallMobile ? "130px" : "145px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: isSmallMobile ? "11px" : "12px",
            color: "#999",
            textAlign: "center",
            zIndex: 10,
            background: "rgba(255, 255, 255, 0.9)",
            padding: "4px 12px",
            borderRadius: "8px",
          }}
        >
          <div>← Swipe to Review | Swipe to Master →</div>
        </div>
      )}

      {/* Swiped boxes */}
      {renderSwipedBoxes()}

      {/* Instructions */}
      {cards.length === 0 && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: "#888",
            fontSize: isMobile ? "18px" : "22px",
            fontWeight: 500,
            textAlign: "center",
            zIndex: 200,
            padding: "0 20px",
          }}
        >
          No cards in this deck.
          <div style={{ marginTop: "20px" }}>
            {swipeCards.left.length > 0 && (
              <button
                onClick={handleGoOverLeftCards}
                style={{
                  padding: isMobile ? "12px 24px" : "10px 20px",
                  fontSize: isMobile ? "14px" : "16px",
                  backgroundColor: "#4caf50",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  cursor: "pointer",
                  display: "block",
                  margin: "0 auto",
                  zIndex: 201,
                }}
              >
                Go over all cards to review
              </button>
            )}
          </div>
        </div>
      )}
      {currentIdx >= cards.length && cards.length > 0 && (
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            color: "#4caf50",
            fontSize: isMobile ? "24px" : "28px",
            fontWeight: 700,
            textAlign: "center",
            zIndex: 200,
            padding: "0 20px",
          }}
        >
          All cards completed!
          {swipeCards.left.length > 0 && (
            <button
              onClick={handleGoOverLeftCards}
              style={{
                marginTop: "20px",
                padding: isMobile ? "12px 24px" : "10px 20px",
                fontSize: isMobile ? "14px" : "16px",
                backgroundColor: "#4caf50",
                color: "#fff",
                border: "none",
                borderRadius: "5px",
                cursor: "pointer",
                display: "block",
                margin: "20px auto 0",
                zIndex: 201,
              }}
            >
              Go over all cards to review
            </button>
          )}
        </div>
      )}

      {/* Keyboard shortcuts help - desktop only */}
      {!isMobile && cards.length > 0 && currentIdx < cards.length && (
        <div
          style={{
            position: "fixed",
            bottom: "185px",
            left: "50%",
            transform: "translateX(-50%)",
            fontSize: "11px",
            color: "#999",
            textAlign: "center",
            zIndex: 10,
            background: "rgba(255, 255, 255, 0.9)",
            padding: "4px 12px",
            borderRadius: "8px",
          }}
        >
          <div>← → Swipe | ↑ Flip | ↓ View image</div>
        </div>
      )}

      {/* Progress bar */}
      {cards.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: isMobile ? "60px" : "20px",
            left: "50%",
            transform: "translateX(-50%)",
            width: isMobile ? "calc(100% - 40px)" : "80%",
            maxWidth: "400px",
            height: isSmallMobile ? "4px" : "6px",
            background: "#e0e0e0",
            borderRadius: "3px",
            overflow: "hidden",
            zIndex: 200,
          }}
        >
          <div
            style={{
              height: "100%",
              background: "linear-gradient(90deg, #1976d2, #4caf50)",
              width: `${((currentIdx / cards.length) * 100)}%`,
              transition: "width 0.3s ease",
            }}
          />
        </div>
      )}

      {/* Fullscreen image modal */}
      {fullscreenImageUrl && (
        <div
          onClick={() => setFullscreenImageUrl(null)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            cursor: "pointer",
          }}
        >
          <img
            src={fullscreenImageUrl}
            alt="Fullscreen"
            onClick={() => setFullscreenImageUrl(null)}
            style={{
              maxWidth: "90vw",
              maxHeight: "90vh",
              objectFit: "contain",
            }}
          />
          <div
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              fontSize: "32px",
              color: "#fff",
              cursor: "pointer",
              fontWeight: "bold",
            }}
          >
            ✕
          </div>
        </div>
      )}
    </div>
  );
};

export default ReadCardPage;

export {};